import getDecorators from "inversify-inject-decorators";
import container from "../../ioc/inversify.config";
import TYPES from "../../ioc/types";
import { ThemeManager } from "../design-system/theme-manager";
import { Selectable, StyleOptions } from "../menu/interfaces";
import { GridLayout, GridLayoutInfo, HorizontalLayout, VerticalLayout } from "../menu/layouts";

const { lazyInject } = getDecorators(container);

export interface HorizontalMenu {
    moveSelectionLeft();
    moveSelectionRight();
}

export interface VerticalMenu {
    moveSelectionUp();
    moveSelectionDown();
}

export interface GridMenu extends HorizontalMenu, VerticalMenu {}

// base menu class, container for menu items
export abstract class BattleVerticalMenu
    extends Phaser.GameObjects.Container
    implements VerticalMenu
{
    private _menuItems: MenuItem[];
    private _menuItemIndex: number;
    private _x: number;
    private _y: number;
    private _selected: boolean;

    constructor(x: number, y: number, scene: Phaser.Scene) {
        super(scene, x, y);
        this._menuItems = [];
        this._menuItemIndex = 0;
        this._x = x;
        this._y = y;
        this._selected = false;
        // Clear _menuItems when Phaser destroys this container so stale listeners
        // cannot iterate destroyed MenuItem objects and call setColor() on them.
        this.once("destroy", () => {
            this._menuItems = [];
            this._menuItemIndex = 0;
            this._selected = false;
        });
    }

    public addMenuItem(text: string): MenuItem {
        const menuItem = new MenuItem(0, this.menuItems.length * 20, text, this.scene);
        // console.log("adding menuItem: ", JSON.parse(JSON.stringify(menuItem)));
        this.menuItems.push(menuItem);
        this.add(menuItem);
        // deals currently with the case when an enemy is dead - the text is empty but we still want a placeholder and
        // this placeholder should not be selectable.
        // Should be handled more beautifully based on some status.
        if (text === "") {
            menuItem.active = false;
        }
        return menuItem;
    }

    // menu navigation
    public moveSelectionUp() {
        if (!this.menuItems || this.menuItems.length === 0) {
            console.warn("Cannot move selection up: menuItems array is empty or null");
            return;
        }
        if (this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].unselect();
        }
        do {
            this._menuItemIndex--;
            if (this.menuItemIndex < 0) this._menuItemIndex = this.menuItems.length - 1;
        } while (this.menuItems[this.menuItemIndex] && !this.menuItems[this.menuItemIndex].active);
        if (this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].select();
        }
    }

    public moveSelectionDown() {
        if (!this.menuItems || this.menuItems.length === 0) {
            console.warn("Cannot move selection down: menuItems array is empty or null");
            return;
        }
        if (this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].unselect();
        }
        do {
            this._menuItemIndex++;
            if (this.menuItemIndex >= this.menuItems.length) this._menuItemIndex = 0;
        } while (this.menuItems[this.menuItemIndex] && !this.menuItems[this.menuItemIndex].active);
        if (this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].select();
        }
    }

    // select the menu as a whole and highlight the choosen element
    public select(index: number) {
        if (!index) index = 0;

        // Defensive check: ensure menuItems exists and is not empty
        if (!this.menuItems || this.menuItems.length === 0) {
            console.warn("Cannot select menu item: menuItems array is empty or null");
            return;
        }

        this.menuItems.forEach((menuItem) => menuItem.unselect());
        this._menuItemIndex = index;

        // this.menuItems[this.menuItemIndex].unselect();
        // this._menuItemIndex = index;

        // Find the next active menu item
        let attempts = 0;
        while (this.menuItems[this.menuItemIndex] && !this.menuItems[this.menuItemIndex].active) {
            this._menuItemIndex++;
            if (this.menuItemIndex >= this.menuItems.length) this._menuItemIndex = 0;
            if (this.menuItemIndex == index || attempts++ > this.menuItems.length) {
                console.warn("No active menu items found");
                return;
            }
        }

        // Final safety check before calling select
        if (this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].select();
            this._selected = true;
        } else {
            console.warn(
                `Cannot select menu item at index ${this.menuItemIndex}: item is undefined`
            );
        }
    }

    // deselect this menu
    public deselect() {
        if (this.menuItems && this.menuItems[this.menuItemIndex]) {
            this.menuItems[this.menuItemIndex].unselect();
        }
        this._menuItemIndex = 0;
        this._selected = false;
    }

    public abstract confirm();

    // clear menu and remove all menu items
    public clear() {
        this.menuItems.forEach((menuItem) => {
            menuItem.active = false;
            menuItem.destroy();
            //            this.remove(menuItem);
        });
        this.menuItems.length = 0;
        this._menuItemIndex = 0;
    }

    get menuItems(): MenuItem[] {
        return this._menuItems;
    }

    get menuItemIndex(): number {
        return this._menuItemIndex;
    }

    set menuItemIndex(index: number) {
        //        console.log('setting menu item to ', index);
        this._menuItemIndex = index;
    }

    get menuX(): number {
        return this._x;
    }

    get menuY(): number {
        return this._y;
    }

    get selected(): boolean {
        return this._selected;
    }
}

export class MenuItem extends Phaser.GameObjects.Text implements Selectable {
    @lazyInject(TYPES.ThemeManager)
    private themeManager: ThemeManager;

    constructor(x: number, y: number, text: string, scene: Phaser.Scene) {
        const themeManager = container.get<ThemeManager>(TYPES.ThemeManager);

        super(scene, x, y, text, {
            color: themeManager.getColorString("text.primary"),
            align: "left",
            fontSize: themeManager.getFontSize("base"),
            fontFamily: themeManager.getFontFamily(),
        });
    }

    public select() {
        if (!this.style) return; // guard: object may have been destroyed by Phaser
        this.setColor(this.themeManager.getColorString("text.selected"));
    }

    public unselect() {
        if (!this.style) return; // guard: object may have been destroyed by Phaser
        this.setColor(this.themeManager.getColorString("text.primary"));
    }

    public destroy() {
        this.active = false;
        this.visible = false;
        super.destroy();
    }
}

export abstract class BaseMenu<T extends Selectable & Phaser.GameObjects.Container>
    extends Phaser.GameObjects.Container
{
    protected _menuItems: T[];
    protected _menuItemIndex: number;
    protected _x: number;
    protected _y: number;
    protected _selected: boolean;

    constructor(scene: Phaser.Scene, x: number, y: number) {
        super(scene, x, y);
        this._menuItems = [];
        this._menuItemIndex = 0;
        this._x = x;
        this._y = y;
        this._selected = false;
        this.bindEvents();
    }

    protected bindEvents() {
        if (this.scene.input.keyboard.listenerCount("keydown") == 0) {
            this.scene.input.keyboard.on("keydown", this.onKeyInput, this);
        }
        this.scene.events.on("shutdown", this.unsubscribe, this);
    }

    public abstract onKeyInput(event: KeyboardEvent);

    protected unsubscribe() {
        this.scene?.input.keyboard.off("keydown", this.onKeyInput, this);
        this.scene?.events.off("shutdown", this.unsubscribe, this);
    }

    public addMenuItem(item: T): T {
        this.menuItems.push(item);
        this.add(item);
        return item;
    }

    public previousMenuItem() {
        if (this._menuItemIndex <= 0) {
            return;
        }
        if (this._menuItems) {
            this._menuItems[this._menuItemIndex].unselect();
            this._menuItemIndex--;
            this._menuItems[this._menuItemIndex].select();
        }
    }

    public nextMenuItem() {
        if (this._menuItemIndex >= this._menuItems.length - 1) {
            return;
        }
        if (this._menuItems) {
            //                console.log('itemIndex: ', this._menuItemIndex, ', lenght: ', this._menuItems.length);
            this._menuItems[this._menuItemIndex].unselect();
            this._menuItemIndex++;
            this._menuItems[this._menuItemIndex].select();
        }
    }

    public abstract confirm();

    // clear menu and remove all menu items
    /*    public clear() {
            for(var i = 0; i < this.menuItems.length; i++) {
                this.menuItems[i].destroy();
            }
            this.menuItems.length = 0;
            this._menuItemIndex = 0;
        }*/

    get menuItems(): T[] {
        return this._menuItems;
    }

    get menuItemIndex(): number {
        return this._menuItemIndex;
    }

    set menuItemIndex(index: number) {
        //            console.log('setting menu item to ', index);
        this._menuItemIndex = index;
    }

    get menuX(): number {
        return this._x;
    }

    get menuY(): number {
        return this._y;
    }

    get selected(): boolean {
        return this._selected;
    }
}

export abstract class DefaultVerticalMenu<T extends Selectable & Phaser.GameObjects.Container>
    extends BaseMenu<T>
    implements VerticalMenu
{
    private layout = new VerticalLayout();

    constructor(scene: Phaser.Scene, x: number, y: number) {
        super(scene, x, y);
    }

    public onKeyInput(event: KeyboardEvent) {
        if (event.code === "ArrowUp") {
            this.moveSelectionUp();
        } else if (event.code === "ArrowDown") {
            //            console.log('current sel: ', this.menuItemIndex,
            //            ', items lenght: ', this.menuItems.length);
            this.moveSelectionDown();
        } else if (event.code === "Space") {
            this.confirm();
        }
    }

    public centerElementsX() {
        this.layout.centerX(this.scene.physics.world.bounds.width, this.menuItems);
    }

    public centerElementsY(styleOptions?: StyleOptions) {
        this.layout.centerY(this.scene.physics.world.bounds.height, this.menuItems, styleOptions);
    }

    // menu navigation
    public moveSelectionUp() {
        this.previousMenuItem();
    }

    public moveSelectionDown() {
        this.nextMenuItem();
    }
}

export abstract class DefaultHorizontalMenu<T extends Selectable & Phaser.GameObjects.Container>
    extends BaseMenu<T>
    implements HorizontalMenu
{
    private layout = new HorizontalLayout();

    constructor(scene: Phaser.Scene, x: number, y: number) {
        super(scene, x, y);
    }

    // menu navigation
    public moveSelectionLeft() {
        this.previousMenuItem();
    }

    public moveSelectionRight() {
        this.nextMenuItem();
    }

    public onKeyInput(event: KeyboardEvent) {
        console.log("horizontal menu key input: ", event.code);
        if (event.code === "ArrowRight") {
            this.moveSelectionRight();
        } else if (event.code === "ArrowLeft") {
            this.moveSelectionLeft();
        } else if (event.code === "Space") {
            console.log("confirming horizontal menu");
            this.confirm();
        }
    }

    public centerElementsX(styleOptions?: StyleOptions) {
        this.layout.centerX(this.scene.physics.world.bounds.width, this.menuItems, styleOptions);
    }

    public centerElementsY() {
        this.layout.centerY(this.scene.physics.world.bounds.height, this.menuItems);
    }
}

export abstract class DefaultGridMenu<T extends Selectable & Phaser.GameObjects.Container>
    extends BaseMenu<T>
    implements HorizontalMenu, VerticalMenu
{
    protected _elementPerLine: number;

    private layout = new GridLayout();
    private gridLayoutInfo: GridLayoutInfo;

    constructor(scene: Phaser.Scene, x: number, y: number, elementsPerLine: number = 1) {
        super(scene, x, y);
        this._elementPerLine = elementsPerLine;
    }

    public computeLayout(paddingBottom?: number) {
        let styleOptions: StyleOptions = undefined;
        if (paddingBottom) {
            styleOptions = new StyleOptions();
            styleOptions.paddingBottom = paddingBottom;
        }
        this.gridLayoutInfo = this.layout.drawGrid(
            this.scene.physics.world.bounds.width,
            this.scene.physics.world.bounds.height,
            this.menuItems,
            styleOptions
        );
    }

    private getCurrentRowAndCol(): { row: number; col: number } {
        if (!this.gridLayoutInfo || !this.gridLayoutInfo.rows) {
            return { row: 0, col: 0 };
        }

        for (let rowIndex = 0; rowIndex < this.gridLayoutInfo.rows.length; rowIndex++) {
            const row = this.gridLayoutInfo.rows[rowIndex];
            const colIndex = row.indexOf(this._menuItemIndex);
            if (colIndex !== -1) {
                return { row: rowIndex, col: colIndex };
            }
        }
        return { row: 0, col: 0 };
    }

    public moveSelectionLeft() {
        const { row, col } = this.getCurrentRowAndCol();
        if (col === 0) {
            return; // Already at leftmost position
        }
        this.menuItems[this._menuItemIndex].unselect();
        this._menuItemIndex = this.gridLayoutInfo.rows[row][col - 1];
        this.menuItems[this._menuItemIndex].select();
    }

    public moveSelectionRight() {
        const { row, col } = this.getCurrentRowAndCol();
        console.log("Current row:", row, "col:", col);
        const currentRow = this.gridLayoutInfo.rows[row];
        if (!currentRow) {
            return;
        }
        if (col >= currentRow.length - 1) {
            return; // Already at rightmost position
        }
        this.menuItems[this._menuItemIndex].unselect();
        this._menuItemIndex = currentRow[col + 1];
        this.menuItems[this._menuItemIndex].select();
    }

    public moveSelectionUp() {
        const { row, col } = this.getCurrentRowAndCol();
        if (row === 0) {
            return; // Already at top row
        }
        const previousRow = this.gridLayoutInfo.rows[row - 1];
        // Move to same column in previous row, or last item if previous row is shorter
        const targetCol = Math.min(col, previousRow.length - 1);
        this.menuItems[this._menuItemIndex].unselect();
        this._menuItemIndex = previousRow[targetCol];
        this.menuItems[this._menuItemIndex].select();
    }

    public moveSelectionDown() {
        const { row, col } = this.getCurrentRowAndCol();
        if (row >= this.gridLayoutInfo.rows.length - 1) {
            return; // Already at bottom row
        }
        const nextRow = this.gridLayoutInfo.rows[row + 1];
        // Move to same column in next row, or last item if next row is shorter
        const targetCol = Math.min(col, nextRow.length - 1);
        this.menuItems[this._menuItemIndex].unselect();
        this._menuItemIndex = nextRow[targetCol];
        this.menuItems[this._menuItemIndex].select();
    }

    public onKeyInput(event: KeyboardEvent) {
        if (event.code === "ArrowUp") {
            this.moveSelectionUp();
        } else if (event.code === "ArrowDown") {
            this.moveSelectionDown();
        } else if (event.code === "ArrowRight") {
            this.moveSelectionRight();
        } else if (event.code === "ArrowLeft") {
            this.moveSelectionLeft();
        } else if (event.code === "Space") {
            this.confirm();
        }
    }
}
